<template>
  <div class="vcl-login">
    <h3 class="vcl-logon__title">{{ title }}</h3>
    <vp-tab-menu
      v-model="activeTag"
      class="vcl-logon__tab"
      :tabs="tabs"
      :styles="{ align: 'space-around', ...tabStyles }"
    >
      <template #password-content>
        <div class="vcl-login__info">
          <p class="vcl-logon__description">{{ $t('signInEmailAndPassword') }}</p>
          <vp-input
            v-model="email"
            class="mb19"
            autocomplete="username"
            width="100%"
            height="50px"
            :placeholder="$t('enterEmail')"
            type="text"
            required
            :styles="inputStyles"
          >
            <template #label>{{ $t('email') }}</template>
          </vp-input>
          <vp-input
            v-model="password"
            class="vcl-login__password mb19"
            autocomplete="current-password"
            width="100%"
            height="50px"
            :placeholder="$t('enterPassword')"
            :type="isPasswordVisible ? 'text' : 'password'"
            required
            :styles="inputStyles"
          >
            <template #label>
              <span class="vcl-login__label">{{ $t('password') }}</span>
              <a
                class="vcl-login__forgot-password vcl-logon__link"
                tabindex="0"
                @click.stop="passwordReset"
                @keyup.enter.stop="passwordReset"
              ><u>{{ $t('forgotPassword') }}</u></a>
            </template>
            <template #suffix>
              <VpIcon
                :icon="isPasswordVisible ? 'eye' : 'invisible-eye'"
                class="vcl-login__eye"
                :class="{ 'vcl-login__eye--visible': isPasswordVisible }"
                :style="eyeIconStyle"
                @click.stop="isPasswordVisible = !isPasswordVisible"
              />
            </template>
          </vp-input>
        </div>
        <vp-button
          class="vcl-logon__button primary large"
          :disabled="!email || !password"
          :styles="buttonStyles"
          @click="passwordLogin"
        >
          {{ $t('signIn') }}
        </vp-button>
      </template>
      <template #secure-link-content>
        <div class="vcl-login__info">
          <p class="vcl-logon__description mb19">{{ $t('signInLink') }}</p>
          <vp-input
            v-model="email"
            style="margin-bottom: 59px"
            autocomplete="username"
            width="100%"
            height="50px"
            :placeholder="$t('enterEmail')"
            type="text"
            :styles="inputStyles"
          >
            <template #label>{{ $t('email') }}</template>
          </vp-input>
          <vp-checkbox v-model="usePin" class="vcl-login__pin mb19" :styles="checkboxStyles">
            <template #label>{{ $t('sendPin') }}</template>
          </vp-checkbox>
        </div>
        <vp-button
          class="vcl-logon__button primary large"
          :disabled="!email"
          :styles="buttonStyles"
          @click.prevent=""
          @mousedown.prevent="passwordless"
        >
          {{ $t('sendSecureLink') }}
        </vp-button>
      </template>
    </vp-tab-menu>
  </div>
</template>

<script>
// General Components
import VpIcon from '../../components/vp-icon/VpIcon.vue';
import VpCheckbox from '../../components/vp-checkbox/VpCheckbox.vue';
import VpInput from '../../components/vp-input/VpInput.vue';
import VpButton from '../../components/vp-button/VpButton.vue';
import VpTabMenu from '../../components/vp-tab-menu/VpTabMenu.vue';

export default {
  name: 'VpLogon',
  components: {
    VpIcon,
    VpCheckbox,
    VpInput,
    VpButton,
    VpTabMenu,
  },
  inject: ['$busy', '$color', '$auth', '$screen'],
  props: {
    styles: {
      type: Object,
      default() {
        return {};
      },
    }
  },
  data() {
    return {
      isPasswordVisible: false,
      activeTag: 'password',
      password: null,
      email: null,
      usePin: false,
    };
  },
  computed: {
    tabs() {
      const tabs = [
        { value: 'password', label: this.$i18n.t('usePassword'), path: '#password' },
        { value: 'secure-link', label: this.$i18n.t('useSecureLink'), path: '#secure-link' },
      ];
      return tabs;
    },
    tabStyles() {
      const defaultStyles = {
        fontSize: '20px',
        tabMargin: '0',
        menuHeight: '50px',
        activeColor: this.$color.lighten(this.styles.primaryColor, -50) || this.styles.tabActiveColor || '#016699',
        activeUnderlineColor: this.$color.lighten(this.styles.primaryColor, 20) || this.styles.tabUnderlineColor || '#F58220',
        inactiveColor: this.styles.tabInactiveColor || '#A6B1C3',
        backgroundColor: this.styles.tabBackgroundColor || '#F4F7F8',
      };

      const mobileStyles = {
        fontSize: '18px',
        menuHeight: '40px',
      };

      return this.$screen.width > 479 ? defaultStyles : { ...defaultStyles, ...mobileStyles };
    },
    checkboxStyles() {
      return this.styles.checkbox || {};
    },
    inputStyles() {
      return this.styles.input || {};
    },
    buttonStyles() {
      return this.styles.button || {};
    },
    eyeIconStyle() {
      return {
        '--eye-icon-color': this.$color.lighten(this.styles.primaryColor, 0) || this.styles.eyeIconColor || '#016699',
      };
    },
    title() {
      switch (this.app) {
        case 'utility':
          return this.$i18n.t('utilitySignIn');
        case 'admin':
          return this.$i18n.t('adminSignIn');
        case 'home':
          return this.$i18n.t('hoSignIn');
        case 'gravity':
          return this.$i18n.t('gravitySignIn');
        default:
          return '';
      }
    },
    who() {
      switch (this.app) {
        case 'utility':
          return 'Utility';
        case 'admin':
          return 'Admin';
        case 'home':
          return 'Homeowner';
        case 'gravity':
          return 'Developer'
        default:
          return 'Unknown';
      }
    },
  },
  created() {
    window.addEventListener('keyup', this.keyListener);
  },
  beforeDestroy() {
    window.removeEventListener('keyup', this.keyListener);
  },
  mounted() {
    this.$busy.on(false);
    document.getElementsByTagName('html')[0].lang = localStorage.getItem('selectedLanguage') || 'en';  
  },
  methods: {
    passwordless() {
      this.$busy.on(true);
      const { logon } = this.$deps;
      return logon.passwordlessLogin({ user: this.email, usePin: this.usePin })
        .then((data) => {
          const { accountId, totp = false } = data;
          if (this.usePin) {
            return this.$router.push({ path: '/auth', query: { pin: true, uid: accountId, totp } });
          }
          return this.$router.push({ path: '/logon/success', query: {} });
        })
        .catch(() => {
          return this.$router.push({ path: '/logon/error', query: {} });
        })
        .finally(() => {
          this.$busy.on(false);
        });
    },
    passwordLogin() {
      this.$busy.on(true);
      const { logon } = this.$deps;
      return logon.passwordLogin({ user: this.email, password: this.password })
        .then((data) => {
          const { loggedIn, reason, token, uid, usingPassword = true } = data;
          
          if (!loggedIn) {
            return reason === 'TOTP' 
              ? this.$router.push({ path: '/totp', query: { token, uid, usingPassword } }) 
              : this.errorHandler(reason);
            }
          this.$auth.set(true);
          return logon.loginRedirect();
        })
        .catch(() => {
          return this.$router.push({ path: '/logon/error', query: {} })
        })
        .finally(() => {
          this.$busy.on(false);
        });
    },
    passwordReset() {
      this.$router.push('/logon/password-reset');
    },
    errorHandler(reason) {
      switch (reason) {
        case 'LOCKED_OUT':
          return this.$router.push({ path: '/logon/error', query: { message: this.$i18n.t('lockedOut') } });
        case 'INCORRECT':
          return this.$router.push({ path: '/logon/error', query: { message: this.$i18n.t('incorrectEmailOrPassword') } });
        case 'TOTP_REQUIRED':
          return this.$router.push({ path: '/logon/error', query: {} });
        default:
          return this.$router.push({ path: '/logon/error', query: {} });
      }
    },
    keyListener(event) {
      if (event.key === 'Enter') {
        if (this.activeTag === 'secure-link' && this.email) {
          return this.passwordless();
        }
        if (this.activeTag === 'password' && this.password && this.email) {
          return this.passwordLogin();
        }

        return null;
      }

      return null;
    },
  },
};
</script>

<style lang="scss">
  @import "../../styles/mixins.scss";
  .vcl-login {
    $login: &;

    padding-bottom:24px;

    &__info {
      margin: 0;
      padding: 29px 47px 16px;
      height:320px;
      @include Mobile--Only {
        padding: 24px 17px;
      }
      #{ $login }__pin {
        margin-top: 74px;
        @include Tablet {
          margin-top: 0;
        }
      }
      #{ $login }__password {
        margin: 0;
        padding: 0;
        display: flex;
        align-items: center;
        .vcl-input__label {
          width: 100%;
          justify-content: space-between;
        }
        #{ $login }__label {
          margin-left: 4px;
        }
        #{ $login }__forgot-password {
          font-size: 14px;
          cursor: pointer;
        }
        #{ $login }__eye {
          color: #AFAFAF;
          margin-right: 6px;
          margin-top: 4px;
          font-size: 18px;
          cursor: pointer;
          &--visible {
            color: var(--eye-icon-color);
          }
        }
      }
    }
  }
</style>
